
import { Stack } from '@mui/material';
import PropTypes from 'prop-types';
import Scrollbar from 'src/components/Scrollbar';
import { fDateTime } from 'src/utils/formatTime';
import { useState, useEffect } from 'react';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import Iconify from 'src/components/Iconify';
import v2_actionDeletarNota from 'src/actions/v2/chats/v2_actionDeletarNota';
import { useDispatch } from 'src/redux/store';
import { getChatInfoByChatId } from 'src/redux/slices/chat';
import useAuth from 'src/hooks/useAuth';

export default function ChatInfoNotas({ chat, user }) {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const dispatch = useDispatch();
    const { cliente } = useAuth();
    const [notas, setNotas] = useState(chat?.notas || []);
    const [deletando, setDeletando] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(null);

    useEffect(() => {
        setNotas(chat?.notas || []);
        setConfirmDelete(null);
        setDeletando(null);
    }, [chat?._id, chat?.notas]);

    const handleDelete = async (nota) => {
        const notaId = nota.id || nota._id;
        if (!notaId) return;
        setDeletando(notaId);
        try {
            await v2_actionDeletarNota(notaId);
            setNotas(prev => prev.filter(n => (n.id || n._id) !== notaId));
            dispatch(getChatInfoByChatId(chat._id, null, user.id, false));
            enqueueSnackbar(t('chat.info.notes.deleted'), { variant: 'success', autoHideDuration: 2000, anchorOrigin: { vertical: 'bottom', horizontal: 'left' } });
        } catch (error) {
            console.log('Erro ao excluir nota', error);
            enqueueSnackbar(t('chat.info.notes.deleteError'), { variant: 'error', autoHideDuration: 3000, anchorOrigin: { vertical: 'bottom', horizontal: 'left' } });
        } finally {
            setDeletando(null);
            setConfirmDelete(null);
        }
    };

    return (
        <Stack>
            <Scrollbar sx={{
                height: {
                    xs: `calc(25vh - 150px)`,
                    sm: `calc(35vh - 150px)`,
                    md: `calc(60vh - 125px)`,
                    lg: `calc(75vh - 125px)`,
                    xl: `calc(82vh - 125px)`
                },
            }}>
                {notas.length === 0 && (
                    <div className="text-center py-6">
                        <div className="w-10 h-10 rounded-xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center mx-auto mb-2">
                            <Iconify icon="eva:file-text-outline" width={20} className="text-gray-400" />
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{t('chat.info.notes.emptyShort')}</p>
                    </div>
                )}

                <div className="space-y-2 p-1">
                    {notas.map((nota, index) => {
                        const notaId = nota.id || nota._id || index;
                        const isConfirming = confirmDelete === notaId;

                        return (
                            <div
                                key={notaId}
                                className="group bg-white dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700 rounded-xl p-3 hover:border-violet-200 dark:hover:border-violet-600 transition-all"
                            >
                                <div className="flex items-start justify-between gap-2">
                                    <div className="flex-1 min-w-0">
                                        <p className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 mb-1">
                                            {fDateTime(nota.date)}
                                        </p>
                                        <p className="text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words">
                                            {nota.body}
                                        </p>
                                    </div>

                                    <div className="flex items-center shrink-0">
                                        {isConfirming ? (
                                            <div className="flex items-center gap-1">
                                                <button
                                                    onClick={() => handleDelete(nota)}
                                                    disabled={deletando === notaId}
                                                    className="p-1 rounded-md bg-red-50 hover:bg-red-100 text-red-500 transition-colors"
                                                    title={t('chat.info.notes.confirmDelete')}
                                                >
                                                    {deletando === notaId ? (
                                                        <Iconify icon="svg-spinners:ring-resize" width={14} />
                                                    ) : (
                                                        <Iconify icon="eva:checkmark-outline" width={14} />
                                                    )}
                                                </button>
                                                <button
                                                    onClick={() => setConfirmDelete(null)}
                                                    className="p-1 rounded-md bg-gray-50 hover:bg-gray-100 text-gray-400 transition-colors"
                                                    title={t('common.buttons.cancel')}
                                                >
                                                    <Iconify icon="eva:close-outline" width={14} />
                                                </button>
                                            </div>
                                        ) : (
                                            <button
                                                onClick={() => setConfirmDelete(notaId)}
                                                className="p-1 rounded-md opacity-0 group-hover:opacity-100 hover:bg-red-50 text-gray-400 hover:text-red-500 transition-all"
                                                title={t('chat.notes.delete')}
                                            >
                                                <Iconify icon="eva:trash-2-outline" width={14} />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </Scrollbar>
        </Stack>
    );
}
